// BaremetalCheckoutFields.jsx
import React, { useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import FormikStyledField from "../../../inputs/form/FormikStyledField";
import FormikBgpSwitch from "../../../inputs/form/FormikBgpSwitch";
import FormikHeadlessListbox from "../../../inputs/form/FormikHeadlessListbox";
import { usePageSettings } from "../../../../../contexts/PageSettingsContext";
import { useFetchServiceAddons } from "../../../../../utils/fetchAddons";
import { PuffLoader } from "react-spinners";
import TabTitle from "../TabTitle";
import { useFormikContext } from "formik";

const PREFERRED_ORDER = ["os", "additionalIp", "bgp"];

// For the framer-motion animations
const fadeInUp = {
	initial: { opacity: 0, y: 20 },
	animate: { opacity: 1, y: 0 },
	exit: { opacity: 0, y: 20 },
};

const BaremetalCheckoutFields = ({ handleAddonChange }) => {
	const { translations } = usePageSettings();

	const {
		data: serviceData,
		isLoading: isLoadingAddons,
		isError: failedLoadingAddons,
	} = useFetchServiceAddons("baremetal");

	const { values, setFieldValue } = useFormikContext();

	// Preselect the first 'additionalIp' option if none is selected
	useEffect(() => {
		if (!serviceData) return;

		const additionalIpAddon = serviceData.find((addon) => addon.type === "additionalIp");
		const selectedId = values?.configuration?.additionalIp?.id;

		if (additionalIpAddon && additionalIpAddon.options.length > 0 && !selectedId) {
			const firstOption = additionalIpAddon.options[0];

			setFieldValue("configuration.additionalIp.id", firstOption.id, false);
			setFieldValue("configuration.additionalIp.name", firstOption.name, false);

			handleAddonChange("additionalIp", firstOption.id, firstOption.name);
		}
	}, [serviceData]);

	// Sort data by your preferred order
	const sortedServiceData = useMemo(() => {
		if (!serviceData) return [];
		return [...serviceData].sort((a, b) => {
			const iA = PREFERRED_ORDER.indexOf(a.type);
			const iB = PREFERRED_ORDER.indexOf(b.type);
			return iA - iB;
		});
	}, [serviceData]);

	if (isLoadingAddons) {
		return (
			<div className="flex flex-grow min-h-[500px] flex-col gap-2 text-center pt-20 items-center">
				<PuffLoader color="#DD097D" />
				<p className="flex gap-1">{translations["ConfigServerFormLoading"]}</p>
			</div>
		);
	}

	if (failedLoadingAddons) {
		return (
			<div className="flex flex-grow min-h-[500px] flex-col gap-2 text-center justify-center items-center">
				<p className="flex gap-1">{translations["ConfigServerFormLoadingFailed"]}</p>
			</div>
		);
	}

	return (
		<AnimatePresence>
			<TabTitle />
			<motion.div
				key="BaremetalCheckoutFields"
				className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-7 gap-x-12 gap-y-7"
				variants={fadeInUp}
				initial="initial"
				animate="animate"
				exit="exit"
			>
				{/* Example: a hostname field */}
				<FormikStyledField
					label={translations["ConfigurationSummaryHostname"]}
					name="configuration.serverName"
					type="text"
					placeholder={translations["serverNamePlaceholder"]}
					defaultValue="servername.example.com"
					required
					tooltip={translations["serverNameTooltip"]}
					translatedErrorMessage={translations["serverNamePlaceholderError"]}
					translations={translations}
				/>

				{sortedServiceData.map((addon) => {
					// If it's a "bgp" type, render your BGP switch:
					if (addon.type === "bgp") {
						return (
							<div key={addon.type} className="flex text-sm gap-2">
								<FormikBgpSwitch
									label={translations["ToggleBGPSession"]}
									name={`configuration.${addon.type}`}
									bgpOptions={addon.options}
									handleAddonChange={handleAddonChange}
								/>
							</div>
						);
					} else {
						// Otherwise, use our Headless UI Listbox
						const fieldName = `configuration.${addon.type}.id`;

						return (
							<div key={addon.type} className="flex text-sm flex-col gap-2">
								<FormikHeadlessListbox
									label={addon.type}
									name={fieldName}
									required={
										addon.type === "os" ||
										addon.type === "hostname" ||
										addon.type === "additionalIp"
									}
									options={addon.options}
									onChange={async (selectedId, form) => {
										// "selectedId" is the new ID the user picked
										const selectedOption = addon.options.find((o) => o.id === selectedId);
										// 1) Update field
										form.setFieldValue(fieldName, selectedId, false);
										form.setFieldValue(
											`configuration.${addon.type}.name`,
											selectedOption?.name || "",
											false
										);
										handleAddonChange(addon.type, selectedId || null, selectedOption.name);
										// 2) Wait a tick for Formik to apply the new field value
										await new Promise((resolve) => setTimeout(resolve, 0));
										// 3) Now do a fresh formik validation pass, after option is *truly* updated in Formik
										await form.validateForm();
									}}
								/>
							</div>
						);
					}
				})}
			</motion.div>
		</AnimatePresence>
	);
};

export default BaremetalCheckoutFields;
